import * as React from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';
import { PDFDocument, rgb } from 'pdf-lib';
import sign from '../assets/sign.png';
import constants from '../constants.js';

const SignPDF = () => {
  const [visible, setVisible] = React.useState(false);
  const [value, setValue] = React.useState([]);
  const [submitvalue, setSubmitvalue] = useState(constants.signPDF);
  const [servicevalue, setServicevalue] = useState([]);
  const [filearray, setfilearray] = useState([]);
  const [totalsize, setTotalsize] = useState(0);
  const [notify, setNotify] = useState(null);
  const [error, setError] = useState(null);
  const [signatureText, setSignatureText] = useState('');
  const [signaturePosition, setSignaturePosition] = useState('bottom-right');

  const toggleModal = React.useCallback(
    (service) => () => {
      setVisible(true);
      setValue('');
      setServicevalue(service);
      setSubmitvalue(constants.signPDF);
      setTotalsize(0);
      setNotify('');
      setError('');
    },
    [],
  );

  const onClickFooterButton = () => {
    setVisible(false);
    setValue('');
    setSubmitvalue(constants.signPDF);
    setTotalsize(0);
    setNotify('');
    setError('');
    setSignatureText('');
    setSignaturePosition('bottom-right');
  };

  async function _handleInputChange(e) {
    setValue(e.detail.value);
    let files = e.detail.value;

    const pdfFiles = files.filter((file) => file.type === constants.pdfFileType);

    if (pdfFiles.length !== files.length) {
      setNotify(constants.onlyPdfAllowed);
      setError('error');
    } else {
      setNotify('');
      setError(null);
    }

    let valueAdded = 0;
    for (const file of pdfFiles) {
      valueAdded += file.size;
    }
    setTotalsize(valueAdded / 1000000);
    setfilearray(pdfFiles);
  }

  async function SigningPDF() {
    setSubmitvalue(<Spinner />);
    let files = filearray;

    if (!signatureText.trim()) {
      setNotify(constants.enterSignatureText);
      setError(constants.error);
      setSubmitvalue(constants.signPDF);
      return;
    }

    for (const file of files) {
      if (file.type === constants.pdfFileType) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const pdfDoc = await PDFDocument.load(arrayBuffer);
          const pages = pdfDoc.getPages();
          const currentDate = new Date().toLocaleDateString();

          pages.forEach((page) => {
            const { width, height } = page.getSize();

            // Position calculations
            let x, y;
            switch (signaturePosition) {
              case 'top-left':
                x = 50;
                y = height - 50;
                break;
              case 'top-right':
                x = width - 150;
                y = height - 50;
                break;
              case 'bottom-left':
                x = 50;
                y = 50;
                break;
              case 'bottom-right':
              default:
                x = width - 150;
                y = 50;
                break;
            }

            // Add signature to this page
            page.drawText(`Signature: ${signatureText}`, {
              x: x,
              y: y,
              size: 12,
              color: rgb(0, 0, 0),
            });

            // Add date below signature
            page.drawText(`Date: ${currentDate}`, {
              x: x,
              y: y - 20,
              size: 10,
              color: rgb(0, 0, 0),
            });
          });

          const pdfBytes = await pdfDoc.save();
          const blob = new Blob([pdfBytes], { type: constants.pdfFileType });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `signed_${file.name}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch (error) {
          console.error('Signing failed:', error);
          setNotify(constants.signingFailed);
          setError(constants.error);
        }
      } else {
        setNotify(constants.unsupportedFileType);
        setError(constants.error);
      }
      setSignatureText('');
      setSignaturePosition('bottom-right');
    }

    setSubmitvalue(constants.success);
    addUsage('AWESOME PDF TOOLS', '1.0', filearray.length, window.location.href);
    if (error) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }

  return (
    <div>
      <div className="service-card" onClick={toggleModal(constants.signPDF)}>
        <div className="service-card-icon">
          <img src={sign} alt="Sign PDF" loading="lazy" />
        </div>
        <h3 className="service-card-title">Sign PDF</h3>
        <p className="service-card-desc">Add digital signature to PDF documents</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={() => {
          setVisible(false);
          setSignatureText('');
          setSignaturePosition('bottom-right');
        }}
        visible={visible}
        size="small"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClickFooterButton}>
                {constants.cancel}
              </Button>
              <div>
                {totalsize < 2000 && totalsize > 0 && filearray.length > 0 && signatureText.trim() ? (
                  <Button variant="primary" onClick={SigningPDF}>
                    {submitvalue}
                  </Button>
                ) : (
                  <Button disabled variant="primary">
                    {totalsize > 2000 ? constants.limitExceeded : submitvalue}
                  </Button>
                )}
              </div>
            </SpaceBetween>
          </Box>
        }
        header={servicevalue}
      >
        {servicevalue === constants.signPDF ? (
          <div>
            <FormField errorText={notify} label={constants.uploadFile} description={constants.onlyPdfSupported}>
              <FileUpload
                onChange={_handleInputChange}
                value={value}
                i18nStrings={{
                  uploadButtonText: (e) => (e ? 'Choose files' : 'Choose'),
                  dropzoneText: (e) => (e ? 'Drop files to upload' : 'Drop file to upload'),
                  removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                  limitShowFewer: 'Show fewer files',
                  limitShowMore: 'Show more files',
                  errorIconAriaLabel: 'Error',
                }}
                multiple
                showFileLastModified={false}
                showFileSize={false}
                showFileThumbnail={false}
                tokenLimit={9}
                constraintText=""
                accept=".pdf"
              />
            </FormField>

            <FormField label={constants.signature} description={constants.enterSignature}>
              <Input
                value={signatureText}
                onChange={({ detail }) => setSignatureText(detail.value)}
                placeholder={constants.enterYourSignature}
              />
            </FormField>

            <FormField label={constants.signaturePosition} description={constants.chooseSignaturePosition}>
              <Select
                selectedOption={{
                  label: signaturePosition.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                  value: signaturePosition,
                }}
                onChange={({ detail }) => setSignaturePosition(detail.selectedOption.value)}
                options={[
                  { label: constants.topLeft, value: 'top-left' },
                  { label: constants.topRight, value: 'top-right' },
                  { label: constants.bottomLeft, value: 'bottom-left' },
                  { label: constants.bottomRight, value: 'bottom-right' },
                ]}
              />
            </FormField>
          </div>
        ) : (
          <div></div>
        )}
        <br />
        {constants.currentSize} {totalsize.toFixed(2)}
        <span>{constants.mb}</span>
        <br />
        {constants.totalMaxLimit}
      </Modal>
    </div>
  );
};

export default SignPDF;
